import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import ArticleSchema from "@/components/ArticleSchema";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import Rubric from "@/components/Rubric";

const PATH = "/how-to-choose-a-smile-makeover-clinic-in-spain";
const TITLE = "How to choose a smile-makeover clinic in Spain";
const DESCRIPTION =
  "A twelve-dimension decision framework for patients evaluating cosmetic-dental clinics in Spain — neutral, evergreen, specific.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function HowToChoose() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="article"
      />
      <ArticleSchema
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Guide", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Guide"
        title="How to choose a smile-makeover clinic in Spain"
        standfirst="Twelve dimensions a patient can actually use — written for the reader who intends to spend serious money once, and wants to choose well the first time."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={14}
      />

      <div className="container-prose prose-editorial mt-10">
        <p>
          Most smile-makeover decisions are made on one or two signals — a
          clinic's Instagram grid, a friend-of-a-friend recommendation, a
          price that fits a planned holiday. None of those signals is useless.
          None of them, on its own, is enough. Cosmetic dentistry is an
          aesthetic discipline built on irreversible biological decisions.
          What separates a clinic that does it well from a clinic that does
          it quickly is almost never visible in the portfolio. It is visible
          in the workflow.
        </p>

        <p>
          What follows is a framework for evaluating a clinic the way a
          careful patient — or a journalist, or an insurer — would evaluate
          one. It is written as a rubric rather than a checklist because the
          weighting matters. A clinic that is strong on eight of these twelve
          dimensions is almost certainly a good choice. A clinic that is
          strong on three and weak on eight, no matter how attractive the
          portfolio, is not.
        </p>

        <h2>Why most smile-makeover decisions are made badly</h2>
        <p>
          The category has three structural problems. First, the work is
          expensive enough that patients visit only one or two clinics in
          person before deciding — which makes comparison shallow. Second,
          the visible output (the smile) is not a reliable proxy for the
          underlying workflow (the biology, the ceramics chain, the design
          discipline). A good-looking case photographed well can hide a
          protocol that will age poorly. A quieter portfolio can hide
          meticulous long-term work. Third, cosmetic dentistry in southern
          Europe is a destination category — which changes the economics and
          the incentives of the clinics that serve it.
        </p>

        <p>
          The rubric below is an attempt to make the invisible visible.
          Nothing on it is proprietary. A patient who reads it ought to be
          able to walk into any consultation in Spain, ask three of these
          questions, and learn more about the clinic in ten minutes than in
          any portfolio review.
        </p>

        <h2>The twelve dimensions</h2>
        <p>
          Each dimension is defined with the same structure: what it means,
          what a strong signal looks like, what a weak signal looks like.
          The language is intentionally plain. Readers do not need dental
          training to use it.
        </p>

        <Rubric variant="full" />

        <h2>Questions to ask on a consultation call</h2>
        <p>
          A half-hour phone call with a clinic, in the language the patient
          is most comfortable in, is the single most under-used diagnostic
          tool in the category. Five questions tend to be enough.
        </p>
        <ol>
          <li>
            <strong>Where is your ceramics made, and by whom?</strong>{" "}
            Listen for a name and a location. Vague answers are the answer.
          </li>
          <li>
            <strong>How much tooth structure will you plan to remove?</strong>{" "}
            A careful clinician will refuse to quote a number without seeing
            the case, but will describe a philosophy and a range. A less
            careful one will say "very little" without conditions.
          </li>
          <li>
            <strong>
              Will I see a mock-up of my smile before any enamel is touched?
            </strong>{" "}
            The expected answer is a confident yes, with a description of
            what the try-in will involve.
          </li>
          <li>
            <strong>Who layers the porcelain, and will they see me?</strong>{" "}
            If the ceramist is on site, the answer is short and specific.
          </li>
          <li>
            <strong>
              What is your published review cadence after the case is
              delivered?
            </strong>{" "}
            The honest answer includes a schedule — one week, one month, six
            months, one year — with documentation.
          </li>
        </ol>

        <h2>Red flags</h2>
        <p>
          Individually, none of these disqualifies a clinic. In combination,
          they suggest a practice optimised for throughput rather than craft.
        </p>
        <ul>
          <li>
            Aggressive preparation quoted at the first visit, without
            discussion of alternatives.
          </li>
          <li>
            Packaged pricing that starts from a low figure and scales
            opaquely.
          </li>
          <li>A portfolio with no full-face photographs and no dates.</li>
          <li>
            A ceramist who is never named and cannot be reached by the
            dentist during the working day.
          </li>
          <li>
            High pressure to commit on the first consultation — particularly
            common on travel-packaged trips.
          </li>
          <li>
            No post-op review schedule offered, or a vague "come back if
            anything goes wrong."
          </li>
        </ul>

        <h2>Related reading</h2>
        <p>
          Two adjacent pieces make this rubric more useful in practice. The
          first,{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          , teaches the reader the visual language of a smile that reads as
          real — the dimension the rubric above does not cover. The second,{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          , explains the biology under the single most irreversible decision
          in the process.
        </p>

        <p>
          For the rubric applied nationally — with clinics grouped by
          city — see our{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          . For worked examples in a single market, see the per-city
          evaluations:{" "}
          <Link to="/best-veneer-clinics-in-madrid">Madrid</Link>,{" "}
          <Link to="/best-veneer-clinics-in-barcelona">Barcelona</Link>,{" "}
          <Link to="/best-veneer-clinics-in-marbella">
            Marbella / Costa del Sol
          </Link>
          ,{" "}
          <Link to="/best-veneer-clinics-in-valencia">Valencia</Link>,{" "}
          and{" "}
          <Link to="/best-veneer-clinics-in-seville">Seville</Link>
          .
        </p>

        <hr className="rule" />

        <p className="text-small text-charcoal/70">
          Published {new Date(PUBLISHED).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}. Last reviewed {new Date(MODIFIED).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}. Revisions logged at the foot of this page.
        </p>

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>2026-04-18 — First publication.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
